import { ClassNames, css } from '@emotion/react';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import useControlled from '@mui/material/utils/useControlled';
import { cloneElement } from 'react';
import { su, theme, typography } from '@arc/frontend/ui-theme';
import { DropdownTransition } from './DropdownTransition';
const hubTooltipStyles = {
  tooltip: /*#__PURE__*/css(typography.regular, su.roundedLg, {
    backgroundColor: theme.colors.white,
    border: `1px solid ${theme.colors.background.headerBorder}`,
    boxShadow: '0px 4px 12px #0000001f',
    padding: '0.75rem',
    whiteSpace: 'normal'
  }, process.env.NODE_ENV === "production" ? "" : ";label:tooltip;"),
  arrow: /*#__PURE__*/css({
    '&::before': {
      backgroundColor: theme.colors.white,
      border: `1px solid ${theme.colors.background.headerBorder}`
      // No shadow on the arrow. It would double up with the tooltip's shadow
      // and look bad.
    }
  }, process.env.NODE_ENV === "production" ? "" : ";label:arrow;")
};
export function HubTooltip({
  styles,
  children,
  ...forwardedProps
}) {
  const [open, setOpen] = useControlled({
    controlled: forwardedProps.open,
    default: false,
    name: 'HubTooltip',
    state: 'open'
  });
  const handleClose = () => {
    setOpen(false);
  };
  const handleOpen = () => {
    setOpen(true);
  };
  const childrenProps = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...children.props
  };
  {
    // WORKAROUND: Open tooltip on click. `enterTouchDelay={0}` does not always
    //             open the tooltip on a fast touch.
    //
    // `handleClose` will be called when MUI's tooltip handles the `mouseleave`
    // event.
    const originalChildrenPropsOnClick = childrenProps.onClick;
    childrenProps.onClick = event => {
      originalChildrenPropsOnClick?.(event);
      handleOpen();
    };
  }
  return <ClassNames>
			{
    // eslint-disable-next-line @typescript-eslint/no-shadow
    ({
      css
    }) => <ClickAwayListener onClickAway={handleClose}>
						<Tooltip arrow TransitionComponent={DropdownTransition} {...forwardedProps}
      // WORKAROUND: We don't use the built in touch behavior. It feels
      //             janky. We use on click instead.
      disableTouchListener open={open} onOpen={handleOpen} onClose={handleClose} slotProps={{
        ...forwardedProps.slotProps,
        popper: {
          ...forwardedProps.slotProps?.popper,
          // On mobile, the default margin is too large. Make it consistent with desktop.
          sx: {
            [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]: {
              marginTop: '12px'
            },
            [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]: {
              marginBottom: '12px'
            },
            [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]: {
              marginLeft: '12px'
            },
            [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
              marginRight: '12px'
            }
          }
        },
        tooltip: {
          ...forwardedProps.slotProps?.tooltip,
          // WORKAROUND: This is to prevent the tooltip from closing when
          //             touching on the tooltip. This is only an issue on
          //             touch devices. Mouse device work fine.
          onPointerDown: e => {
            // Prevents a mouseleave event from firing on the trigger
            // element when the tooltip is touched.
            e.preventDefault();
          },
          sx: {
            width: 'max-content',
            maxWidth: '100vw'
          }
        }
      }} classes={{
        popper: css(styles?.popper),
        tooltip: css(hubTooltipStyles.tooltip, styles?.tooltip),
        arrow: css(hubTooltipStyles.arrow)
      }}>
							<span>{cloneElement(children, childrenProps)}</span>
						</Tooltip>
					</ClickAwayListener>}
		</ClassNames>;
}